<template>
  <div>
    <menu-content-container
      :chip="$t('forms.video_properties')"
      chip-secondary
      border-full
    >
      <content-row>
        <t-input
          ref="videoSrcInput"
          v-model="videoSrc"
          data-testid="video-src"
          :label="$t('forms.video_src')"
          :placeholder="placeholder"
          :disabled="disabledSrc"
          @on-blur="parseSrc()"
        ></t-input>
      </content-row>
      <content-row>
        <t-auto-complete
          v-model="alt"
          data-testid="video-alt"
          :label="$t('forms.image_alt')"
          :placeholder="$t('forms.image_alt')"
          :has-icon="true"
          :flat-merge-tags="mergeTagsStore.flatMergeTags"
          :grouped-merge-tags="mergeTagsStore.mergeTagsForInputs"
          :smart-merge-tags="mergeTagsStore.smartMergeTags"
          :disabled="disabledAlt"
          @on-blur="mutationEmptyString = ''"
        ></t-auto-complete>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.video_size')"
      :header-margin-bottom="6"
    >
      <content-row :margin-top="16">
        <t-slider
          v-model="width"
          :max="containerWidth"
          :min="30"
          unit="px"
          data-testid="video-size-slider"
          :disabled="disabledWidth"
        ></t-slider>
      </content-row>
    </menu-content-container>

    <menu-content-container border-full>
      <TLabel
        :changed="align.changed"
        :tooltip="$t('forms.reset_value')"
        data-testid="align-label"
        @reset="align.reset"
        >{{ $t("forms.image_align") }}</TLabel
      >
      <content-row justify-between :margin-top="12">
        <TButtonGroup>
          <TGroupButton
            data="left"
            small
            data-testid="align-left"
            :active="align.model === 'left'"
            :disabled="disabledAlign"
            @button-clicked="align.model = 'left'"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            small
            data-testid="align-center"
            :active="align.model === 'center'"
            :disabled="disabledAlign"
            @button-clicked="align.model = 'center'"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            small
            data-testid="align-right"
            :active="align.model === 'right'"
            :disabled="disabledAlign"
            @button-clicked="align.model = 'right'"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>

        <t-switch
          v-model="fluidOnMobile.model"
          data-testid="fluid-on-mobile"
          :label="$t('forms.fluid_on_mobile')"
          :disabled="disabledFluid"
          :changed="fluidOnMobile.changed"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="fluidOnMobile.reset"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="image-border-radius"
      :label="$t('forms.border_radius')"
      :min-y="0"
      :max-y="containerWidth"
      :min-x="0"
      :max-x="containerWidth"
      :changed="borderRadius.changed"
      :default-value="borderRadius.defaultValue"
      @reset="borderRadius.reset"
    ></borderRadiusForm>

    <padding-form
      data-testid="video-margin"
      :label="$t('forms.video_margin')"
      :disabled="disabledPadding"
      :changed="padding.changed"
      :default-value="padding.defaultValue"
      @reset="padding.reset"
    ></padding-form>

    <menu-content-container :border-bottom="false">
      <content-row>
        <hideOnMobile></hideOnMobile>
      </content-row>
      <content-row :margin-top="16" :margin-bottom="20">
        <hide-on-desktop></hide-on-desktop>
      </content-row>
    </menu-content-container>
  </div>
</template>

<script lang="ts" setup>
  import hideOnMobile from "./components/hideOnMobile.vue";
  import hideOnDesktop from "./components/hideOnDesktop.vue";
  import paddingForm from "./paddingForm.vue";
  import borderRadiusForm from "./borderRadiusForm.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useMergeTagsStore } from "@/stores/mergeTags";
  import { useBlockAttr } from "@/composables/useBlockAttr";
  import { getThumbnail } from "../utils/getVideoThumbnail";

  import { AligmentLeft, AligmentCenter, AligmentRight } from "@topol/icons";
  import {
    TInput,
    TButtonGroup,
    TGroupButton,
    TLabel,
    TMergetagsAutocomplete as TAutoComplete,
    TSwitch,
    TSliderClassic as TSlider,
  } from "@topol/twig-ui";

  import { useI18n } from "vue-i18n";
  import { computed, getCurrentInstance, ref } from "vue";
  import validHref from "@/utils/validHref";

  const app = getCurrentInstance();

  if (app === null) {
    throw new Error("App is not defined");
  }

  const mjmlStore = useMjmlStore();
  const mergeTagsStore = useMergeTagsStore();
  const { t } = useI18n();
  const { str, bool, nullable } = useBlockAttr({ mode: "active" });

  // --- Composable bindings ---
  const fluidOnMobile = bool("fluid-on-mobile", "true", "false");
  const borderRadius = nullable("border-radius");
  const padding = nullable("padding");
  const align = str("align", "center");

  const videoSrcInput = ref();
  const mutationEmptyString = ref("");

  const placeholder = t("forms.video_src");

  const videoSrc = computed({
    get() {
      if (mutationEmptyString.value === "videoSrc") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("videoSrc") ?? "";
    },
    async set(url: string) {
      if ((!url || url.trim() === "") && mjmlStore.mjml.langs) {
        // If the value is empty, we set the mutationEmptyString to true
        // to avoid immediate display of the value from the primary mutation.
        mutationEmptyString.value = "videoSrc";
      } else {
        mutationEmptyString.value = "";
      }
      // set videoSrc as received url
      mjmlStore.updateBlockAttribute({
        prop: "videoSrc",
        value: url.trim(),
      });

      // set href as received url
      mjmlStore.updateBlockAttribute({
        prop: "href",
        value: url.trim(),
      });

      // set src as obtained thumbnail
      mjmlStore.updateBlockAttribute({
        prop: "src",
        value: await getThumbnail(url.trim()),
      });
    },
  });

  const disabledSrc = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("videoSrc");
  });

  const disabledFluid = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("fluid-on-mobile");
  });

  const containerWidth = computed(() => {
    return mjmlStore.getActiveBlockAttrInt("containerWidth");
  });

  const width = computed({
    get() {
      const width = mjmlStore.getActiveBlockAttrInt("width");
      if (width !== undefined) {
        if (isNaN(width)) {
          return containerWidth.value;
        }

        if (width > containerWidth.value) {
          return containerWidth.value;
        }

        return mjmlStore.getActiveBlockAttrInt("width");
      }

      return containerWidth.value;
    },
    set(val: number) {
      mjmlStore.updateBlockAttribute({
        prop: "width",
        value: val,
      });
    },
  });

  const disabledWidth = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("width");
  });

  const disabledAlign = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("align");
  });

  const disabledPadding = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("padding");
  });

  const alt = computed({
    get() {
      if (mutationEmptyString.value === "alt") {
        return "";
      }
      return mjmlStore.getActiveBlockAttrString("alt");
    },

    set(value: string) {
      if (value !== alt.value) {
        if (!value && mjmlStore.mjml.langs) {
          // If the value is empty, we set the mutationEmptyString to true
          // to avoid immediate display of the value from the primary mutation.
          mutationEmptyString.value = "alt";
        } else {
          mutationEmptyString.value = "";
        }
        mjmlStore.updateBlockAttribute({
          prop: "alt",
          value: value,
        });
      }
    },
  });

  const disabledAlt = computed(() => {
    return mjmlStore.getActiveBlockPropDisabled("alt");
  });

  function parseSrc() {
    mutationEmptyString.value = "";
    if (!validHref(videoSrc.value)) {
      videoSrc.value = "http://" + videoSrc.value;
    }
  }
</script>
